import React from 'react';
import { Grid, Box, Typography, IconButton, Tooltip } from '@mui/material';
import {
  KeyboardArrowUp as ChevronUpIcon,
  KeyboardArrowDown as ChevronDownIcon,
  Info,
  Warning,
  Check,
  Close,
} from '@mui/icons-material';
import { GridItemStyle } from './styled';

import dayjs from 'dayjs';

// Reusable sortable header component
const SortableHeader = ({ field, label, currentSort, onSort }) => {
  const isActive = currentSort.field === field;


  return (
    <Box
      onClick={() => onSort(field)}
      sx={{
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        color: isActive ? 'primary.main' : 'inherit',
        transition: 'color 0.2s ease',
        '&:hover': {
          color: 'primary.main'
        }
      }}
    >
      <Typography
        variant="body1"
        component="span"
        sx={{
          fontWeight: isActive ? 500 : 400,
        }}
      >
        {label}
      </Typography>
      {isActive && (
        <IconButton
          size="small"
          sx={{
            padding: '2px',
            marginLeft: '2px',
            color: 'inherit'
          }}
        >
          {currentSort.dir === 'asc'
            ? <ChevronUpIcon sx={{ fontSize: '1.1rem' }} />
            : <ChevronDownIcon sx={{ fontSize: '1.1rem' }} />
          }
        </IconButton>
      )}
    </Box>
  );
};

const BookingDesktopView = ({ bookings, filters, handleSortChange, navigateBooking, statusMapping, typeMapping, t, bookingAction }) => {
  // Helper object for current sort state
  const currentSort = {
    field: filters.order_by,
    dir: filters.order_dir
  };

  const headers = [
    { field: 'planned_start_day', label: t('Date'), width: 3 },
    { field: 'status', label: t('Status'), width: 2 },
    { field: 'booking_type', label: t('Type'), width: 2 },
    { field: 'code', label: t('Code'), width: 1 },
    { field: 'tenant__name', label: t('Customer'), width: 2 },
    { field: 'action', label: t('Action'), width: 2 }
  ];

  return (
    <>
      
      {/* Header */}
      <Box sx={{ marginBottom: 2 }}>
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 1,
            '& .MuiGrid-item': {
              paddingTop: '0 !important',
              paddingBottom: '0 !important'
            }
          }}
        >
          {headers.map(header => (
            <Grid
              item
              xs={header.width}
              key={header.field}
              sx={{ height: '40px', display: 'flex', alignItems: 'center' }}
            >
              <SortableHeader
                field={header.field}
                label={header.label}
                currentSort={currentSort}
                onSort={handleSortChange}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Rows */}
      {bookings?.map((item, index) => {
        const formattedStartTime = item?.planned_start_time ? dayjs(item.planned_start_time, 'HH:mm').format('HH:mm') : '';
        const formattedEndTime = item?.planned_end_time ? dayjs(item.planned_end_time, 'HH:mm').format('HH:mm') : '';
        const formattedDate = `${dayjs(item?.planned_start_day).format('DD/MM/YYYY')} ${formattedStartTime} - ${formattedEndTime}`;
        const formattedTenant = item?.tenant?.name || '';


        return (
          <Grid
            container
            spacing={2}
            key={index}
            sx={{
              height: '48px',
              alignItems: 'center',
              borderRadius: '4px',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: 'action.hover',
                cursor: 'pointer'
              },
              '& .MuiGrid-item': {
                paddingTop: '0 !important',
                paddingBottom: '0 !important'
              }
            }}
            onClick={() => navigateBooking(item)}
          >
            {/* Using the same widths as headers */}
            <Grid item xs={3} sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
              <GridItemStyle>
                <Typography variant="body2">{formattedDate}</Typography>
              </GridItemStyle>
            </Grid>
            <Grid item xs={2} sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center'
            }}>
              <GridItemStyle>
                <Typography
                  variant="body2"
                  sx={{
                    color: item?.status === 'PC' ? 'danger.sad' : 'inherit',
                  }}
                >
                  {statusMapping[item?.status]}
                </Typography>
              </GridItemStyle>
            </Grid>
            <Grid item xs={2} sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
              <GridItemStyle>
                <Typography variant="body2">{typeMapping[item?.booking_type]}</Typography>
              </GridItemStyle>
            </Grid>
            <Grid item xs={1} sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
              <GridItemStyle>
                <Typography variant="body2">{item?.code}</Typography>
              </GridItemStyle>
            </Grid>
            <Grid item xs={2} sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
              <GridItemStyle>
                <Typography variant="body2">{formattedTenant}</Typography>
              </GridItemStyle>
            </Grid>
            <Grid item xs={2} sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              position: 'relative'
            }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateBooking(item);
                }}
              >
                <Info fontSize="small" />
              </IconButton>
              {item?.status === 'PC' && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: 'error.main',
                    color: 'common.white',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontSize: '0.875rem',
                    transition: 'background-color 0.4s ease',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      '& .actions': {
                        opacity: 1,
                        visibility: 'visible'
                      },
                      '& .alert': {
                        opacity: 0,
                        visibility: 'hidden'
                      }
                    }
                  }}
                >
                  <Box
                    className="alert"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      transition: 'all 0.4s ease',
                      width: '100%',
                      justifyContent: 'center'
                    }}
                  >
                    <Warning fontSize="small" />
                    <Typography variant="body2">Attention</Typography>
                  </Box>

                  <Box
                    className="actions"
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      gap: 1,
                      opacity: 0,
                      visibility: 'hidden',
                      transition: 'all 0.4s ease'
                    }}
                  >
                    <Tooltip title="Confirm Booking" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          bookingAction(item?.slug, 'accept');
                        }}
                        sx={{

                          marginLeft: '10px',
                          color: 'success.main',
                          backgroundColor: 'white',
                          '& .MuiSvgIcon-root': {
                            fontSize: '1rem'
                          },
                          '&:hover': {
                            backgroundColor: 'white'
                          }
                        }}
                      >
                        <Check />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Decline Booking" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          bookingAction(item?.slug, 'reject');
                        }}
                        sx={{
                          marginLeft: '10px',
                          color: 'error.main',
                          backgroundColor: 'white',
                          '& .MuiSvgIcon-root': {
                            fontSize: '1rem'
                          },
                          '&:hover': {
                            backgroundColor: 'white'
                          }
                        }}
                      >
                        <Close />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        );
      })}
    </>
  );
};

export default BookingDesktopView;